/**
 * Puzzle 1 — The Seized Crane.
 *
 * Basil holds a pressure plate (Pneumatic Anchor) and uses Turn-Table Touch
 * on the crane lever to align it through its notches. When the lever reaches
 * the target notch the crane swings and the blocking redwood log slides
 * away, clearing the path forward.
 *
 * Minna's AuraSight reveals the hidden wiring between the pressure plate
 * and the lever (registered via `ctx.registerInteractive` and a connection
 * line owned by the level shell).
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { Animation } from "@babylonjs/core/Animations/animation";

import { PressurePlate } from "../../../entities/interactives/PressurePlate";
import { CraneLever } from "../../../entities/interactives/CraneLever";
import { CraneVisuals } from "../../art/CraneVisuals";

import { GameEvents } from "../../../config/events";
import { COLORS, SCRAPYARD } from "../../../config/GameConfig";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const CUE = {
  plate: { cat: "minna", parts: [{ text: "onto the brass plate" }] },
  grip: { cat: "basil", parts: [{ action: "turnStart" }, { text: "grip the lever" }] },
  spin: { cat: "basil", parts: [{ action: "rhythmHit" }, { text: "on the beat" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Lift the seized crane";
const PLATE_ANCHOR = { x: 15, y: 4.4, z: 0 };
const LEVER_ANCHOR = { x: 22, y: 6.5, z: 0 };

/** Key 1 within this range of the lever (but outside the 3u reach) gets a
 *  "get closer" toast. */
const TOO_FAR_HINT_RADIUS = 7;

export class SeizedCranePuzzle implements PuzzleModule {
  readonly id = "seizedCrane";
  readonly displayName = "The Seized Crane";

  private ctx!: PuzzleContext;

  private cranePressurePlate!: PressurePlate;
  private craneLever!: CraneLever;
  private craneLogMesh!: Mesh;
  private craneLogAggregate!: PhysicsAggregate;
  private logMat!: PBRMaterial;
  private craneVisuals: CraneVisuals | null = null;

  /** Minimal crane-arm rig (Q-039): a pivot with a brass boom + cable
   *  hanging over the log. Purely visual — no physics, no shadow
   *  casting — it exists so the log clear reads as "the crane hauled
   *  the log away" instead of a despawn. */
  private craneArmPivot!: TransformNode;
  private craneArmMat!: StandardMaterial;

  private platePressed = false;
  private leverAligned = false;
  private cleared = false;

  // Turn-Table Touch state — only owned by this puzzle because the lever
  // is the only valid target within it.
  private turnTableActive = false;
  /** Sim time of the last "get closer" toast (rate limit). */
  private elapsed = 0;
  private lastTooFarToastAt = -Infinity;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Pressure plate ──
    this.cranePressurePlate = new PressurePlate(scene, {
      key: "cranePlate",
      position: new Vector3(15, 3.2, 0),
      label: "Crane Pressure Plate",
    });

    // ── Crane lever (Turn-Table Touch target) ──
    this.craneLever = new CraneLever(scene, {
      key: "craneLever",
      position: new Vector3(22, 5.3, 0),
      label: "Crane Lever",
      totalNotches: SCRAPYARD.PUZZLE.CRANE_NOTCHES,
      targetNotch: SCRAPYARD.PUZZLE.CRANE_TARGET,
    });

    // ── Crane log — blocking mesh with static physics ──
    this.craneLogMesh = MeshBuilder.CreateCylinder(
      "craneLog",
      { diameter: 1.5, height: 4, tessellation: 12 },
      scene,
    );
    this.craneLogMesh.position = new Vector3(25, 3, 0);
    this.craneLogMesh.rotation.z = Math.PI / 2;
    this.logMat = new PBRMaterial("logMat", scene);
    this.logMat.albedoColor = Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_BARK);
    this.logMat.metallic = 0;
    this.logMat.roughness = 0.9;
    this.craneLogMesh.material = this.logMat;
    this.craneLogAggregate = new PhysicsAggregate(
      this.craneLogMesh,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.5 },
      scene,
    );

    // ── Crane arm rig (visual only) ──
    // Pivot behind the play plane, above the lever (x=22) / log (x=25),
    // with a boom reaching east and a cable dropping toward the log.
    // NOT added to any ShadowGenerator (L1 caster trim, commit cd1202f),
    // not pickable, no physics body. Its world matrices only dirty
    // during the one 1.5s clear animation.
    this.craneArmPivot = new TransformNode("craneArmPivot", scene);
    this.craneArmPivot.position = new Vector3(22.5, 8.5, -2);

    this.craneArmMat = new StandardMaterial("craneArmMat", scene);
    this.craneArmMat.diffuseColor = Color3.FromHexString(COLORS.BRASS).scale(0.55);
    this.craneArmMat.specularColor = Color3.Black();
    // Emissive kept ≤0.1 so the GlowLayer doesn't beacon it (Q-037 lesson).
    this.craneArmMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.06);

    const craneBoom = MeshBuilder.CreateBox(
      "craneBoom",
      { width: 5.5, height: 0.28, depth: 0.28 },
      scene,
    );
    craneBoom.parent = this.craneArmPivot;
    craneBoom.position = new Vector3(2.2, 0, 0);
    craneBoom.material = this.craneArmMat;
    craneBoom.isPickable = false;
    craneBoom.receiveShadows = false;

    const craneCable = MeshBuilder.CreateCylinder(
      "craneCable",
      { diameter: 0.07, height: 3.6 },
      scene,
    );
    craneCable.parent = this.craneArmPivot;
    craneCable.position = new Vector3(4.6, -1.8, 0);
    craneCable.material = this.craneArmMat;
    craneCable.isPickable = false;
    craneCable.receiveShadows = false;

    this.craneVisuals = new CraneVisuals(scene, this.craneLogMesh,
      this.craneArmPivot, [craneBoom, craneCable], ctx.shadowGenerator);

    // ── Register AuraSight interactives ──
    ctx.registerInteractive({
      mesh: this.cranePressurePlate.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.craneLever.rootMesh,
      hidden: false,
      state: "revealed",
    });

    // ── Event subscriptions ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_ACTIVATED, (key) => {
        if (key === "cranePlate") {
          this.platePressed = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: "platePressed",
          });
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_DEACTIVATED, (key) => {
        if (key === "cranePlate") this.platePressed = false;
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CRANE_LEVER_ALIGNED, (key) => {
        if (key === "craneLever") {
          this.leverAligned = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: "leverAligned",
          });
          this.clearCraneLog();
        }
      }),
    );

    // Turn-Table Touch rhythm hit → spin the lever, but ONLY if the
    // pressure plate is held down. Without the plate gate, the lever
    // could be solved without ever standing on the plate, which broke
    // the puzzle's "both cats cooperate" intent — flagged in the
    // 2026-04-14 L1 QA pass. Failing silently is also bad UX, so we
    // surface a HUD toast on the first wasted hit attempt to teach
    // the constraint instead of leaving the player guessing.
    let warnedNoPlate = false;
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        if (!this.turnTableActive) return;
        if (!this.platePressed) {
          if (!warnedNoPlate) {
            warnedNoPlate = true;
            ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
              message: "The crane lever won't budge — the brass plate needs weight on it. Minna has to stand on the plate while Basil spins.",
            });
          }
          return;
        }
        // Reset the warning latch so subsequent unanchorings re-warn.
        warnedNoPlate = false;
        this.craneLever.spin();
      }),
    );
  }

  update(_dt: number, elapsed: number): void {
    this.elapsed = elapsed;
    const { minna, basil } = this.ctx;

    // Pressure plate check (both cats can press it)
    this.cranePressurePlate.checkPressure([minna.mesh, basil.mesh]);

    // Alignment can arrive from inside attemptHit's event dispatch. Retire
    // its display at the next fixed step, once that call has returned, instead
    // of leaving the completed rhythm ring until Basil walks out of range.
    if (this.cleared) {
      this.craneLever.unhighlight();
      if (this.turnTableActive) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
      }
      return;
    }

    // Crane lever proximity highlight
    const basilDistCrane = Vector3.Distance(
      basil.mesh.position,
      this.craneLever.rootMesh.position,
    );
    if (basilDistCrane < 3) {
      this.craneLever.highlight();
    } else {
      this.craneLever.unhighlight();
      // Auto-deactivate Turn-Table Touch if Basil walks away from the lever
      if (this.turnTableActive) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
      }
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // 1 = toggle Turn-Table Touch on the crane lever (when Basil is near)
    if (key === "1" && pressed && !this.cleared) {
      const basilPos = this.ctx.basil.mesh.position;
      const dist = Vector3.Distance(basilPos, this.craneLever.rootMesh.position);
      if (dist < 3) {
        if (!this.turnTableActive) {
          // Route through the holder (mirrors the deactivation path
          // below) so ABILITY_ACTIVATED fires for HUD/telemetry/banter.
          // Latching on the boolean also fixes the latent desync where
          // turnTableActive went true even when activation was refused
          // (mid-air/cooldown).
          this.ctx.turnTableTouch.setTarget(
            this.craneLever.rootMesh.position,
            () => {},
          );
          this.turnTableActive =
            this.ctx.basilAbilities.activateAbility("turnTableTouch");
        } else {
          this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
          this.turnTableActive = false;
        }
        return true;
      }
      // Near but not in reach (e.g. trailing a few steps behind on the
      // lever's platform): say so instead of failing silently. Still let
      // other puzzles handle key "1".
      if (dist < TOO_FAR_HINT_RADIUS && this.elapsed - this.lastTooFarToastAt > 3) {
        this.lastTooFarToastAt = this.elapsed;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Closer — Basil needs to be right at the crane lever to spin it.",
        });
      }
      return false;
    }

    // 2 = rhythm hit (only consumed while Turn-Table Touch is active on the lever).
    // Re-check proximity on every hit — if Basil has fallen off the
    // platform, the auto-deactivate in update() fires on the next frame,
    // but between the fall and that frame a scripted trace can still
    // land the rhythm hit. Gemini 3.1-pro fluidity review caught this:
    // "Basil falls off the platform... but is still able to trigger the
    // lever ability. TT Touch VFX plays in mid-air near the lever
    // without his character model present." Gate the hit on proximity.
    if (key === "2" && pressed && this.turnTableActive) {
      const basilPos = this.ctx.basil.mesh.position;
      const dist = Vector3.Distance(basilPos, this.craneLever.rootMesh.position);
      if (dist >= 3) {
        // Basil is no longer close enough; deactivate TT Touch now
        // rather than waiting for the next update() frame.
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
        return false;
      }
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getCue(): PuzzleCue | null {
    if (this.cleared) return null;
    if (!this.platePressed) return { objective: OBJECTIVE, cue: CUE.plate, anchor: PLATE_ANCHOR };
    return { objective: OBJECTIVE, cue: this.turnTableActive ? CUE.spin : CUE.grip, anchor: LEVER_ANCHOR };
  }

  getState(): PuzzleState {
    if (this.cleared) return { solved: true, progress: 1 };
    const steps = [this.platePressed, this.leverAligned].filter(Boolean).length;
    return {
      solved: false,
      progress: steps / 2,
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "cranePressurePlate", position: { x: 15, y: 3.2 }, radius: 1.2, allowedCat: "either", label: "Crane pressure plate" },
        // craneLever: TT Touch interact range is 3u (per SeizedCrane
        // onAbilityKey distance check). Radius reflects the real
        // interact distance, not just the mesh-center catch radius,
        // so the navigator's arrival tolerance matches actual gameplay
        // reach. craneLever mesh is at x=22 (p1_3 east edge) but any
        // cat within 3u counts as "at the lever" for gameplay.
        { id: "craneLever",         position: { x: 22, y: 5.3 }, radius: 3.0, allowedCat: "basil", label: "Crane lever (TT Touch target; 3u interact range)" },
        { id: "craneLog",           position: { x: 25, y: 3 },   radius: 2.0, allowedCat: "either", label: "Crane log (lifted target)" },
        // Staging anchors for the p1_1 → p1_2 → p1_3 staircase.
        { id: "p1_1_east",          position: { x: 11.5, y: 2.2 }, radius: 0.6, allowedCat: "either", label: "p1_1 east edge (jump start to p1_2)" },
        { id: "p1_2_east",          position: { x: 16.5, y: 3.2 }, radius: 0.6, allowedCat: "basil", label: "p1_2 east edge (jump start to p1_3)" },
      ],
      traversals: [
        // p1_1 → p1_2 running jump. plat_top 2.2 → 3.2, peak ≈4.9.
        {
          id: "running_jump_p1_1_to_p1_2",
          kind: "jump",
          cat: "either",
          from: "p1_1_east",
          to: "cranePressurePlate",
          successWindowMs: 3000,
          recipe: [
            { key: "d", holdMs: 100 },
            { key: " ", holdMs: 80, delayMs: 0 },
            { key: "d", holdMs: 900, delayMs: 0 },
          ],
        },
        // p1_2 → p1_3 running jump. plat_top 3.2 → 4.2.
        {
          id: "running_jump_p1_2_to_p1_3",
          kind: "jump",
          cat: "basil",
          from: "p1_2_east",
          to: "craneLever",
          successWindowMs: 3000,
          recipe: [
            { key: "d", holdMs: 100 },
            { key: " ", holdMs: 80, delayMs: 0 },
            { key: "d", holdMs: 900, delayMs: 0 },
          ],
        },
      ],
    };
  }

  getInternals() {
    return {
      id: this.id,
      displayName: this.displayName,
      cranePressurePlate: this.cranePressurePlate,
      craneLever: this.craneLever,
      // Alias as `dial` so the generic `forceSpin interactive:"dial"`
      // pattern works across puzzles with a rotatable lever/dial.
      dial: this.craneLever,
      // Visual crane-arm rig — exposed for the swing-animation contract
      // (tests/QA assert it exists and is animated on clear).
      craneArmPivot: this.craneArmPivot,
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     platePressed?: boolean,   // flip platePressed flag
   *     leverAligned?: boolean,   // mark lever aligned (skips rhythm step)
   *     solved?: boolean,         // fully clear the crane log + emit PUZZLE_SOLVED
   *   }
   *
   * The most useful seed for a trace isolating the log-clear animation is
   * `{ solved: true }`; to pre-stage through the plate step for a trace
   * working the lever spin, use `{ platePressed: true }`.
   */
  onForceSolve(): void {
    this.seed({ solved: true });
  }

  seed(state: Record<string, unknown>): void {
    if (state.platePressed === true) {
      this.platePressed = true;
    }
    if (state.leverAligned === true && !this.leverAligned) {
      this.leverAligned = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "leverAligned",
      });
      // clearCraneLog is idempotent via its `if (this.cleared) return` guard.
      this.clearCraneLog();
    }
    if (state.solved === true && !this.cleared) {
      this.platePressed = true;
      this.leverAligned = true;
      this.clearCraneLog();
    }
  }

  dispose(): void {
    this.craneVisuals?.dispose();
    this.cranePressurePlate.dispose();
    this.craneLever.dispose();
    // Crane-arm rig: recursive dispose takes the boom + cable meshes
    // down with the pivot; the shared material is disposed separately.
    this.craneArmPivot?.dispose();
    this.craneArmMat?.dispose();
    // craneLogMesh + aggregate may already be disposed in clearCraneLog's
    // animation callback. Guard with try/catch to tolerate mid-animation
    // scene teardown.
    try {
      this.craneLogAggregate.dispose();
    } catch {
      /* already disposed */
    }
    try {
      this.craneLogMesh.dispose();
    } catch {
      /* already disposed */
    }
    this.logMat.dispose();
  }

  // ─────────────────────────────────────────────────────────────────

  private clearCraneLog(): void {
    if (this.cleared) return;
    this.cleared = true;

    const { scene } = this.ctx;

    // Animate log sliding down and fading
    const fps = 30;
    const frames = 45; // 1.5s

    // Crane-arm swing: wrench free, overshoot, settle — same 30fps /
    // 45-frame envelope as the log slide so the arm visibly hauls while
    // the log drops+fades (Q-039: the clear previously read as a
    // despawn because nothing else moved on CRANE_LEVER_ALIGNED).
    const armAnim = new Animation(
      "craneArmSwing",
      "rotation.z",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    armAnim.setKeys([
      { frame: 0, value: 0 },
      { frame: 14, value: -0.38 },
      { frame: 32, value: -0.22 },
      { frame: frames, value: -0.28 },
    ]);
    this.craneArmPivot.animations = [armAnim];
    scene.beginAnimation(this.craneArmPivot, 0, frames, false);

    const posAnim = new Animation(
      "craneLogSlide",
      "position.y",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    posAnim.setKeys([
      { frame: 0, value: this.craneLogMesh.position.y },
      { frame: frames, value: this.craneLogMesh.position.y - 10 },
    ]);

    const visAnim = new Animation(
      "craneLogFade",
      "visibility",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    visAnim.setKeys([
      { frame: 0, value: 1 },
      { frame: frames, value: 0 },
    ]);

    // Dispose the physics body BEFORE running the animation. Havok's
    // static body rewrites the mesh transform every pre-step, so if the
    // aggregate is still live the position.y animation is clobbered to
    // the body's stored value each frame and the log visually snaps
    // instead of sliding. Gemini 3.1-pro fluidity review caught this as
    // "crane log vanishes instantly / pops out of existence" — the
    // animation was firing, just invisible because Havok won.
    try {
      this.craneLogAggregate.dispose();
    } catch {
      /* already disposed */
    }

    this.craneVisuals?.prepareLogFade(visAnim, frames);
    this.craneLogMesh.animations = [posAnim, visAnim];
    scene.beginAnimation(this.craneLogMesh, 0, frames, false, 1, () => {
      this.craneVisuals?.finishLog();
      try {
        this.craneLogMesh.dispose();
      } catch {
        /* already disposed */
      }
    });

    this.craneLever.solve();
    this.cranePressurePlate.solve();
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
      puzzleId: this.id,
    });
  }
}
